/** Shared utilities for dictation hotkey hooks. */

import { chronicleSessionIdAtom } from '@/atoms/chronicle';
import { useDictationStore } from '@/features/dictation/dictationBubble/store/useDictationStore';
import { useSessionRecordingStore } from '@/lib/audio/useSessionRecordingStore/useSessionRecordingStore';
import { getDefaultStore } from 'jotai';

/** Checks if dictation can start (no session recording, no Chronicle recording, not processing). */
export const canStartDictation = (): boolean => {
  const isSessionRecording = useSessionRecordingStore.getState().status === 'RECORDING';
  const isChronicleRecording = getDefaultStore().get(chronicleSessionIdAtom) !== null;
  const status = useDictationStore.getState().status;
  return !isSessionRecording && !isChronicleRecording && status !== 'PROCESSING';
};

/** Returns true if session recording is active. */
export const isSessionRecordingActive = (): boolean =>
  useSessionRecordingStore.getState().status === 'RECORDING';

/** Checks if dictation is in an error state that should be reset. */
export const isInErrorState = (): boolean => {
  const status = useDictationStore.getState().status;
  return status === 'ERROR' || status === 'INSERTION_ERROR';
};

/** Checks if dictation is in a stopped/inactive state. FEEDBACK counts: the
 *  post-success prompt is lingering UI, not an active dictation, so the hotkey
 *  must be able to start a new one (mirrors the start() guard in the store). */
export const isInStoppedState = (): boolean => {
  const status = useDictationStore.getState().status;
  return (
    status === 'INACTIVE' ||
    status === 'ERROR' ||
    status === 'INSERTION_ERROR' ||
    status === 'FEEDBACK'
  );
};

/** Resets dictation store if currently in an error state. */
export const resetIfInErrorState = (): void => {
  if (isInErrorState()) {
    useDictationStore.getState().reset();
  }
};
